"use client"

import { useState } from "react"
import { useTheme } from "next-themes"
import { LogoWelltrainer } from "@/components/design-new/marca-welltrainer"
import { useBrandTheme } from "@/lib/contexts/brand-theme-context"

interface BrandLogoProps {
  /** Largura máxima do logo em px */
  maxWidth?: number
  /** Altura máxima do logo em px */
  height?: number
  className?: string
}

export function BrandLogo({ maxWidth = 140, height = 30, className }: BrandLogoProps) {
  const { logoUrl, logoUrlLight, logoTitulo, logoEscala } = useBrandTheme()
  const { resolvedTheme } = useTheme()
  /* Largura dividida pela altura do arquivo. Só ele sabe a forma, então é
     medido no `onLoad`; até lá vale 2, que fica no meio do caminho entre o
     emblema quadrado e o lockup deitado. */
  const [proporcao, setProporcao] = useState<number | null>(null)

  /* ESCURO primeiro, porque o app nasce escuro: é a versão que o aluno vê por
     padrão. Cada tema cai no outro arquivo quando o seu não existe, senão a
     pessoa que mandou um logo só ficava sem marca nenhuma na metade das telas.
     Antes da montagem o `resolvedTheme` é `undefined`, e cair no escuro é o que
     casa com o HTML que o servidor mandou. */
  const activeLogo = resolvedTheme === "light" ? logoUrlLight || logoUrl : logoUrl || logoUrlLight

  if (activeLogo) {
    const escala = logoEscala || 1
    /* A altura sai da FORMA do logo, e não de um número fixo: o logo de
       academia é quase sempre um emblema quadrado cheio de detalhe, e na altura
       de um lockup deitado ele vira uma bolinha ilegível. Mirando a mesma
       largura, quanto mais quadrado, mais alto. Mesma regra da navegação do
       painel (`Marca`, em design-new/cabecalho.tsx). */
    const alvoDeLargura = logoTitulo ? maxWidth * 0.6 : maxWidth
    /* A escala entra DEPOIS do teto da forma: dentro dele, um emblema quadrado
       já batia no limite em 100% e a faixa de tamanho não mudava nada. */
    /* Teto folgado pelo mesmo motivo da navegação: o arquivo costuma vir com
       margem transparente em volta, e caixa apertada deixa sobrar pouca tinta. */
    const alturaPelaForma = Math.min(Math.max(alvoDeLargura / (proporcao ?? 2), 16), height * 2)
    const scaledHeight = Math.min(alturaPelaForma * escala, height * 3.2)
    const scaledMaxWidth = logoTitulo ? maxWidth * 0.6 : maxWidth
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <img
          src={activeLogo}
          alt="Logo"
          onLoad={(e) => {
            const img = e.currentTarget
            if (img.naturalHeight > 0) setProporcao(img.naturalWidth / img.naturalHeight)
          }}
          style={{
            maxWidth: scaledMaxWidth,
            height: scaledHeight,
            objectFit: "contain",
          }}
        />
        {logoTitulo && (
          <span
            className="font-display font-bold text-foreground leading-tight"
            style={{ fontSize: Math.max(12, height * 0.4) }}
          >
            {logoTitulo}
          </span>
        )}
      </div>
    )
  }

  /* Sem arquivo enviado, o nome escrito É a marca. Antes o nome só era desenhado
     ao lado do logo do personal, então quem ligou "Mostrar o seu nome" sem subir
     imagem nenhuma continuava vendo a marca Welltrainer, e a chave parecia não
     fazer nada. Mesma decisão da `Marca` do v2 (design-new/cabecalho.tsx). */
  if (logoTitulo) {
    return (
      <span
        className={`font-display font-bold text-foreground leading-tight truncate ${className || ""}`}
        style={{ fontSize: Math.max(14, height * 0.55), maxWidth }}
      >
        {logoTitulo}
      </span>
    )
  }

  /* Sem logo do personal, a marca é a NOSSA, e ela vem da peça do design system.
     Eram dois SVGs soltos em `/images` (`logo-horizontal-com-simbolo*.svg`), com
     o "well" em lime e o "trainer" em branco: no escuro, lado a lado com o
     painel do personal (onde a marca sai inteira no acento), era outra marca. A
     `LogoWelltrainer` também é a única que acompanha quem trocou a cor do app,
     porque no escuro ela pinta o desenho com `var(--d-acento)` por máscara. */
  /* 18px, e não a `height` recebida: ela é o teto do EMBLEMA do personal, que
     costuma ser quadrado e por isso pede mais altura para se ler. A nossa marca
     é um lockup deitado de 7,5:1, em que 18px já rendem 135px de largura: com a
     altura do emblema (26px no app do aluno) ela saía 44% maior que a mesma
     marca no painel do personal, que usa 18. É a altura do `Cabecalho` do
     sistema no celular. */
  return <LogoWelltrainer altura={Math.min(height, 18)} className={className} />
}

/** Salva a logo URL no cache local (chamado pelo BrandThemeProvider) */
export function cacheBrandLogo(url: string | null) {
  try {
    if (url) {
      localStorage.setItem("wt_brand_logo", url)
    } else {
      localStorage.removeItem("wt_brand_logo")
    }
  } catch {}
}
